import { createContext, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";

import { InitialLocationContext } from "custom-types/FinderPage";
import { Location } from "custom-types/Location";
import {
  DISABLE_LOCATION_CHANGE_RELOAD,
  ENABLE_LOCATION_CHANGE_RELOAD,
} from "redux/reducers/header";
import { getLocationState } from "redux/selectors/location";
import { getState } from "utils/finder/getContextData";
import {
  buildRoutePath,
  getLocationSlug,
  normalizeFinderLocation,
} from "utils/locationUtils";
import { clearAllQueryParams } from "utils/nextRouterUtils";

import MapContext from "./MapContext";

export type LocationContextProps = {
  finderLocation: Location;
};

export const locationContext = {
  finderLocation: {} as Location,
};

const LocationContext = createContext<LocationContextProps>(locationContext);

LocationContext.displayName = "FinderLocationContext";

export const LocationProvider = ({
  data,
  children,
}: {
  data: InitialLocationContext;
  children: React.ReactNode;
}) => {
  const { setBoundingBox } = useContext(MapContext);
  const dispatch = useDispatch();
  const userLocation = useSelector(getLocationState);
  const [currentUserLocation, setCurrentUserLocation] = useState(userLocation);
  const router = useRouter();
  const { query, push } = router;
  const isLocationPage = getState(query);

  const [finderLocation, setFinderLocation] = useState<Location>({
    ...data.finderLocation,
    geoSlug: getLocationSlug({
      city: data.finderLocation?.city,
      state: data.finderLocation?.state || "",
      stateCode: data.finderLocation?.stateCode,
    }),
  });

  const handleUpdateLocation = (location: Location) => {
    clearAllQueryParams(false, ["filter"]);
    setBoundingBox();
    const normalizedLocation = normalizeFinderLocation(location);
    if (normalizedLocation && normalizedLocation !== finderLocation) {
      normalizedLocation && setFinderLocation(normalizedLocation);
    }
  };

  const handleLocationPage = (location: Location) => {
    const { city, state } = location;
    push(`/dispensaries${buildRoutePath({ city, state })}`);
  };

  useEffect(() => {
    dispatch({ type: DISABLE_LOCATION_CHANGE_RELOAD });

    return () => {
      dispatch({ type: ENABLE_LOCATION_CHANGE_RELOAD });
    };
  }, []);

  useEffect(() => {
    if (
      currentUserLocation.coordinates.lat !== userLocation.coordinates.lat ||
      currentUserLocation.coordinates.lon !== userLocation.coordinates.lon
    ) {
      handleUpdateLocation(userLocation);
      setCurrentUserLocation(userLocation);
      isLocationPage && handleLocationPage(userLocation);
    }
  }, [userLocation]);

  return (
    <LocationContext.Provider
      value={{
        finderLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
